import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// Import state
import * as state from './state'

// Import methods
import { fetchContent, fetchLinkPreview } from './methods/content-methods'
import { updateCommentCount, publishPost, openExternalLink } from './methods/action-methods'

/**
 * Composable for managing post inspection
 * @param {Object} options - Configuration options
 * @param {string|number} options.postId - ID of the post to inspect
 * @param {string} options.assetId - Asset ID
 * @param {Function} options.emit - Function to emit updates to parent component
 * @returns {Object} Post inspector state and methods
 */
export function usePostInspector(options = {}) {
  let { postId, commentId, assetId, emit = () => {} } = options
  
  // Make sure we extract values from refs
  if (postId && typeof postId === 'object' && postId.__v_isRef) {
    postId = postId.value
  }
  
  if (commentId && typeof commentId === 'object' && commentId.__v_isRef) {
    commentId = commentId.value
  }
  const router = useRouter()
  const { t } = useI18n()
  
  // Reset the loading state to false by default to prevent stuck loading
  state.loading.value = false
  state.content.value = null // Reset content to null on initialization
  
  // Create computed properties that depend on t
  const platformName = state.createPlatformName(t)
  const parsedHashtags = state.createParsedHashtags()
  const isImageLink = state.createIsImageLink()

  // Navigate to analytics page
  const navigateToAnalytics = () => {
    if (!state.content.value?.id) return
    router.push({
      name: 'social-media-analytics',
      params: { id: state.content.value.id },
    })
  }

  // Initialize - Note: fetchContent will manage loading state internally
  onMounted(async () => {
    try {
      if (postId) {
        // Logging removed
        await fetchContent(postId, emit)
      }
    } catch (error) {
      console.error('Error initializing post inspector:', error)
    }
  })

  return {
    // State
    content: state.content,
    loading: state.loading,
    linkPreview: state.linkPreview,
    isContentPublishing: state.isContentPublishing,
    commentCount: state.commentCount,
    
    // Computed
    platformName,
    parsedHashtags,
    isImageLink,
    
    // Methods
    fetchContent: (id) => fetchContent(id || postId, emit),
    fetchLinkPreview,
    updateCommentCount,
    publishPost: () => publishPost(postId, assetId, emit),
    navigateToAnalytics,
    openExternalLink,
  }
}
